import { WebsocketProvider } from 'y-websocket';
import * as Y from 'yjs';

import { getEnv } from '../../lib/env';

import { getUserColor } from './user-color';

export type WebsocketConnectionState = {
  ws: WebsocketProvider;
  yjsDocument: Y.Doc;
  isDisposing: boolean;
};

export type WebsocketAwarenessState = {
  userId: string;
  name: string;
  initials: string;
  activeField?: string;
  color?: string;
  lightColor?: string;
};

const connections = new Map<string, WebsocketConnectionState>();

function disposeConnection(roomId: string) {
  if (!connections.has(roomId)) return;

  const connection = connections.get(roomId)!;
  if (connection.isDisposing) return;

  connection.isDisposing = true;

  const { ws, yjsDocument } = connection;

  ws.shouldConnect = false;
  ws.awareness.destroy();
  ws.disconnect();
  ws.destroy();

  if (!yjsDocument.isDestroyed) {
    yjsDocument.destroy();
  }

  connections.delete(roomId);
}

export function getWebSocketConnection(apiKey: string, roomId: string) {
  if (!connections.has(roomId)) {
    const ydoc = new Y.Doc();
    const websocketEnpoint = getEnv('VITE_COLLABORATION_ENDPOINT');

    const ws = new WebsocketProvider(
      websocketEnpoint,
      `ws/editor/${roomId}`, // roomId = "contentType/id"
      ydoc,
      {
        params: { apiKey },
        connect: true,
      },
    );

    connections.set(roomId, { ws, yjsDocument: ydoc, isDisposing: false });

    const userData = JSON.parse(window.localStorage['cms.user']).data;

    // Set user information for the connection
    ws.awareness.setLocalState({
      userId: userData.id,
      name: userData.firstName + ' ' + userData.lastName,
      initials: userData.firstName[0] + userData.lastName[0],
      color: getUserColor(userData.id),
      lightColor: getUserColor(userData.id, true),
    } as WebsocketAwarenessState);
  }

  return connections.get(roomId)!;
}

export function disconnectFromRoom(roomId: string) {
  disposeConnection(roomId);
}

export const sendRefetchSignalToWsServer = (
  contentTypeName: string,
  id?: string,
) => {
  if (!contentTypeName) return;

  const roomId = `${contentTypeName}/${id || 'add'}`;
  const wsConnection = connections.get(roomId);

  if (!wsConnection?.ws?.ws) return;

  const nativeSocket = wsConnection.ws.ws;
  if (nativeSocket.readyState !== WebSocket.OPEN) return;

  const docKey = contentTypeName && id ? `${contentTypeName}/${id}` : undefined;

  const payload = docKey ? { type: 'refetch', docKey } : { type: 'refetch' };

  try {
    nativeSocket.send(JSON.stringify(payload));
  } catch {
    // fire-and-forget
  }
};
